import { API_BASE_URL } from '../config/constants.js';
import { ApiError, type ApiErrorCode } from './http-error.js';
import { tokenStorage } from './token-storage.js';
import { goToIamAuth } from './silentAuth.js';
import { requestId } from './request-id.js';

interface RequestInit {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  isMultipart?: boolean;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Record<string, unknown> = {};
  try {
    body = (await res.clone().json()) as Record<string, unknown>;
  } catch {
    // non-JSON response
  }

  const codeMap: Record<number, ApiErrorCode> = {
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    409: 'CONFLICT',
    422: 'VALIDATION',
    429: 'RATE_LIMITED',
    423: 'LOCKED',
  };

  const bodyCode = typeof body['code'] === 'string' ? (body['code'] as ApiErrorCode) : undefined;
  const code: ApiErrorCode = bodyCode ?? codeMap[res.status] ?? 'UNKNOWN';
  const rawMessage = typeof body['message'] === 'string' ? body['message'] : res.statusText;
  // If errors is a plain string it's a more descriptive message than the generic one
  const errorsString = typeof body['errors'] === 'string' ? body['errors'] : undefined;
  const message = errorsString ?? rawMessage;
  const errors = Array.isArray(body['errors'])
    ? (body['errors'] as Array<{ path: string; message: string }>)
    : undefined;

  return new ApiError(res.status, code, message, errors);
}

async function parseOk<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

class ApiClient {
  private buildHeaders(isMultipart: boolean): Headers {
    const headers = new Headers();
    const token = tokenStorage.getAccess();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (!isMultipart) headers.set('Content-Type', 'application/json');
    headers.set('X-Request-Id', requestId());
    return headers;
  }

  private async doFetch(path: string, init: RequestInit): Promise<Response> {
    const isMultipart = init.isMultipart === true;
    const headers = this.buildHeaders(isMultipart);
    const method = init.method ?? 'GET';
    const body = init.body !== undefined
      ? isMultipart
        ? (init.body as FormData)
        : JSON.stringify(init.body)
      : null;

    return fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body,
      ...(init.signal !== undefined ? { signal: init.signal } : {}),
      credentials: 'omit',
    });
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    // Capture before the fetch: only redirect to IAM when the caller had an active session.
    // An anonymous visitor hitting a protected endpoint should get the error thrown normally,
    // not be bounced through the IAM silent-auth loop (which lands on the canonical host).
    const hadToken = tokenStorage.getAccess() != null;
    const res = await this.doFetch(path, init);

    if (res.ok) return parseOk<T>(res);

    // No refresh token under the IAM handoff model — a 401 means the in-memory access token
    // is gone/expired. Try a silent re-authorize (returns to the current path); if the loop
    // guard suppresses it, signal the AuthProvider to drop to the manual login fallback.
    // Guard: only attempt re-auth when there was a token in the first place (i.e. an
    // authenticated session expired). Anonymous 401s just throw — no redirect.
    if (res.status === 401) {
      tokenStorage.clear();
      if (hadToken) {
        const attempted = goToIamAuth(window.location.pathname + window.location.search);
        if (!attempted) window.dispatchEvent(new CustomEvent('auth:session-expired'));
      }
    }

    throw await parseError(res);
  }

  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'GET', ...(signal ? { signal } : {}) });
  }

  post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'POST', ...(body !== undefined ? { body } : {}), ...(signal ? { signal } : {}) });
  }

  put<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'PUT', ...(body !== undefined ? { body } : {}), ...(signal ? { signal } : {}) });
  }

  patch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', ...(body !== undefined ? { body } : {}), ...(signal ? { signal } : {}) });
  }

  delete<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'DELETE', ...(signal ? { signal } : {}) });
  }

  postMultipart<T>(path: string, body: FormData, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'POST', body, isMultipart: true, ...(signal ? { signal } : {}) });
  }
}

export const apiClient = new ApiClient();
